import { useCallback, useEffect, useState } from "react";
import { CalendarOff, Share2, Copy, Trash2, Loader2, Link2, Download, Eye, MessageSquare, Star, TrendingUp, TrendingDown, Minus, ListTodo } from "lucide-react";
import { CreateActionsDialog } from "@/components/ActionPlan";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtD, fmtDT, todayISO } from "@/lib/api";
import { Card, Field } from "@/components/kit";
import { cn } from "@/lib/utils";
import { DemoHint } from "@/components/DemoTour";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SHARES_EVENT = "ka-shares-changed";
const BLANK = { date_from: "", date_to: "", reason: "" };

export function ClosuresCard({ venueId, onChanged }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState(BLANK);
  const load = useCallback(() => api.get("/records/closures", { params: { venue_id: venueId } }).then((r) => setData(r.data)), [venueId]);
  useEffect(() => {
    load();
  }, [load]);
  const done = () => {
    load();
    onChanged();
  };
  const toggleDay = async (i) => {
    const weekdays = data.weekdays.includes(i) ? data.weekdays.filter((d) => d !== i) : [...data.weekdays, i];
    try {
      await api.put("/records/closures/weekdays", { venue_id: venueId, weekdays });
      toast.success("Weekly closed days updated");
      done();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/records/closures", { venue_id: venueId, date_from: f.date_from, date_to: f.date_to || f.date_from, reason: f.reason.trim() || "Closed" });
      toast.success("Closure added");
      setF(BLANK);
      done();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const remove = async (id) => {
    await api.delete(`/records/closures/${id}`);
    toast.success("Closure removed");
    done();
  };
  if (!data) return null;
  return (
    <Card data-testid="closures-card">
      <div className="flex items-center gap-2 font-display text-xl font-extrabold"><CalendarOff className="h-5 w-5 text-slate-500" />Planned closed days</div>
      <p className="mt-1 text-sm text-slate-500">No checks are due on these days, so they never show as missed.</p>
      <div className="ka-eyebrow mt-5 mb-2">Closed every week on</div>
      <div className="flex flex-wrap gap-1.5">
        {DAYS.map((d, i) => <button key={d} data-testid={`closed-weekday-${i}`} aria-pressed={data.weekdays.includes(i)} onClick={() => toggleDay(i)} className={cn("ka-press rounded-full px-3 py-1.5 text-sm font-semibold", data.weekdays.includes(i) ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200")}>{d}</button>)}
      </div>
      <div className="ka-eyebrow mt-6 mb-2">Dates or date ranges</div>
      <form onSubmit={add} className="grid gap-3 sm:grid-cols-2">
        <Field label="From *"><input data-testid="closure-from-input" type="date" required className="ka-input" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></Field>
        <Field label="To" hint="Leave blank for one day"><input data-testid="closure-to-input" type="date" min={f.date_from} className="ka-input" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></Field>
        <Field label="Reason" className="sm:col-span-2"><input data-testid="closure-reason-input" maxLength={120} className="ka-input" placeholder="e.g. Christmas, refurbishment" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></Field>
        <button data-testid="closure-add-button" className="ka-press justify-self-start rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">Add closure</button>
      </form>
      <ul className="mt-4 divide-y divide-slate-100">
        {data.ranges.map((c) => (
          <li key={c.id} data-testid={`closure-${c.id}`} className="flex items-center gap-3 py-2 text-sm">
            <span className="flex-1"><b>{fmtD(c.date_from)}{c.date_to !== c.date_from && ` – ${fmtD(c.date_to)}`}</b><span className="text-slate-500"> · {c.reason}</span></span>
            <button data-testid={`closure-delete-${c.id}`} onClick={() => remove(c.id)} className="ka-press rounded-full p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function ShareButton({ venueId, month, label }) {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/records/share", { venue_id: venueId, month });
      setLink(data);
      window.dispatchEvent(new Event(SHARES_EVENT));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <span className="relative inline-flex">
      <button data-testid="share-inspector-button" disabled={busy || !venueId || month > todayISO().slice(0, 7)} onClick={create} className="ka-press inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold hover:border-slate-900 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}Share with inspector</button>
      <DemoHint testid="demo-hint-share" tip="Creates a secure 7-day link an inspector can open without logging in" />
      </span>
      <Dialog open={!!link} onOpenChange={(o) => !o && setLink(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Inspector link for {label}</DialogTitle></DialogHeader>
          {link && <>
            <p className="text-sm text-slate-600">Anyone with this link can view {label} and download the PDF. No login needed. It stops working on <b>{fmtDT(link.expires_at)}</b>, and you can cancel it any time.</p>
            <div data-testid="share-link-text" className="break-all rounded-lg bg-slate-100 p-3 font-mono text-xs">{link.url}</div>
            <button data-testid="share-copy-button" onClick={() => { navigator.clipboard.writeText(link.url); toast.success("Link copied"); }} className="ka-press inline-flex items-center justify-center gap-1 rounded-full bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"><Copy className="h-4 w-4" />Copy link</button>
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}

function VisitLog({ id }) {
  const [rows, setRows] = useState(null);
  const [fb, setFb] = useState([]);
  useEffect(() => {
    api.get(`/records/share/${id}/visits`).then((r) => setRows(r.data));
    api.get(`/records/share/${id}/feedback`).then((r) => setFb(r.data));
  }, [id]);
  if (!rows) return <p className="mt-2 text-xs text-slate-500">Loading…</p>;
  return (
    <div className="mt-2 w-full space-y-2">
      {fb.map((f, i) => (
        <div key={i} data-testid={`share-feedback-${id}-${i}`} className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-950">
          <div className="flex items-center gap-2 text-xs font-semibold"><MessageSquare className="h-3.5 w-3.5" />{f.name}{f.rating !== null && f.rating !== undefined && <span className="rounded-full bg-sky-200 px-2 py-0.5">Rating {f.rating}/5</span>}<span className="font-normal text-sky-700">{fmtDT(f.at)}</span></div>
          {f.comment && <p className="mt-1 whitespace-pre-line">{f.comment}</p>}
        </div>
      ))}
      <ul data-testid={`share-visits-${id}`} className="space-y-1 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
        {rows.map((v, i) => <li key={i} className="flex items-center gap-1.5">{v.kind === "pdf" ? <Download className="h-3 w-3" /> : <Eye className="h-3 w-3" />}{v.kind === "pdf" ? "Downloaded PDF" : "Opened link"} · {fmtDT(v.at)}</li>)}
        {!rows.length && <li>Not opened yet.</li>}
      </ul>
    </div>
  );
}

const rTone = (r) => (r >= 4 ? "bg-emerald-600" : r >= 3 ? "bg-amber-500" : "bg-rose-600");
const monthName = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

function Trend({ rated }) {
  if (rated.length < 2) return null;
  const diff = rated[0].rating - rated[1].rating;
  const [I, cls, txt] = diff > 0 ? [TrendingUp, "text-emerald-700", `Up ${diff} from ${rated[1].rating}/5`] : diff < 0 ? [TrendingDown, "text-rose-700", `Down ${-diff} from ${rated[1].rating}/5`] : [Minus, "text-slate-500", "Same as last time"];
  return <span data-testid="rating-trend" className={cn("inline-flex items-center gap-1 text-sm font-semibold", cls)}><I className="h-4 w-4" />{txt}</span>;
}

export function RatingHistory({ venueId }) {
  const [rows, setRows] = useState(null);
  const [making, setMaking] = useState(null);
  useEffect(() => {
    const load = () => api.get("/records/feedback", { params: { venue_id: venueId } }).then((r) => setRows(r.data));
    load();
    window.addEventListener("ka-actions-changed", load);
    return () => window.removeEventListener("ka-actions-changed", load);
  }, [venueId]);
  if (!rows) return null;
  const rated = rows.filter((f) => f.rating !== null && f.rating !== undefined);
  const chart = [...rated].reverse().slice(-12);
  return (
    <Card data-testid="rating-history-card" className="lg:col-span-2">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-display text-xl font-extrabold"><Star className="h-5 w-5 text-slate-500" />Inspector rating history</div>
        {rated[0] && <span data-testid="rating-latest" className={cn("rounded-full px-2.5 py-0.5 text-sm font-bold text-white", rTone(rated[0].rating))}>Latest {rated[0].rating}/5</span>}
        <Trend rated={rated} />
      </div>
      {!rows.length && <p className="mt-2 text-sm text-slate-500">No inspector feedback yet. When an inspector leaves a rating or comment on a shared link, it appears here.</p>}
      {chart.length > 1 && (
        <div data-testid="rating-chart" className="mt-5 flex h-28 items-end gap-2">
          {chart.map((f, i) => (
            <div key={i} className="flex flex-1 flex-col items-center gap-1" title={`${f.rating}/5 · ${fmtD(f.at)}`}>
              <span className="text-xs font-bold">{f.rating}</span>
              <div className={cn("w-full max-w-[36px] rounded-t-md", rTone(f.rating))} style={{ height: `${Math.max(6, (f.rating / 5) * 80)}px` }} />
              <span className="font-mono text-[10px] text-slate-500">{new Date(f.at).toLocaleDateString("en-GB", { month: "short", year: "2-digit" })}</span>
            </div>
          ))}
        </div>
      )}
      <ol className="mt-5 space-y-4 border-l-2 border-slate-200 pl-5">
        {rows.map((f, i) => (
          <li key={i} data-testid={`rating-history-item-${i}`} className="relative">
            <span className={cn("absolute -left-[31px] top-0 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white", f.rating !== null && f.rating !== undefined ? rTone(f.rating) : "bg-slate-400")}>{f.rating ?? "–"}</span>
            <div className="text-sm"><b>{f.name}</b><span className="text-slate-500"> · {fmtDT(f.at)} · reviewed {monthName(f.month)}</span></div>
            {f.rating !== null && f.rating !== undefined && <div className="text-xs font-semibold text-slate-600">Rating {f.rating}/5</div>}
            {f.comment && <p className="mt-1 whitespace-pre-line rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{f.comment}</p>}
            <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs">
              {f.actions_total > 0 && <span data-testid={`rating-actions-progress-${i}`} className="font-semibold text-slate-600">{f.actions_done} of {f.actions_total} actions fixed</span>}
              <button data-testid={`rating-make-actions-${i}`} onClick={() => setMaking(f)} className="inline-flex items-center gap-1 font-semibold text-emerald-700 hover:underline"><ListTodo className="h-3.5 w-3.5" />{f.actions_total ? "Add more actions" : "Turn into actions"}</button>
            </div>
          </li>
        ))}
      </ol>
      {making && <CreateActionsDialog venueId={venueId} feedback={making} onClose={() => setMaking(null)} />}
    </Card>
  );
}

export function SharesCard({ venueId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/records/share", { params: { venue_id: venueId } }).then((r) => setRows(r.data)), [venueId]);
  useEffect(() => {
    load();
    window.addEventListener(SHARES_EVENT, load);
    return () => window.removeEventListener(SHARES_EVENT, load);
  }, [load]);
  const revoke = async (id) => {
    await api.delete(`/records/share/${id}`);
    toast.success("Link cancelled");
    load();
  };
  if (!rows.length) return null;
  return (
    <Card data-testid="shares-card">
      <div className="flex items-center gap-2 font-display text-xl font-extrabold"><Link2 className="h-5 w-5 text-slate-500" />Inspector links</div>
      <ul className="mt-3 divide-y divide-slate-100">
        {rows.map((s) => (
          <li key={s.id} data-testid={`share-${s.id}`} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
            <span className="min-w-0 flex-1"><b>{new Date(`${s.month}-01T12:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</b>
              <span className="block text-xs text-slate-500">By {s.created_by} · {s.status === "active" ? `expires ${fmtDT(s.expires_at)}` : s.status} · viewed {s.views}×{s.last_viewed_at && `, last ${fmtDT(s.last_viewed_at)}`}{s.feedback > 0 && <b data-testid={`share-feedback-count-${s.id}`} className="text-sky-700"> · {s.feedback} feedback</b>}</span></span>
            <button data-testid={`share-visits-toggle-${s.id}`} onClick={() => setOpen(open === s.id ? null : s.id)} className="text-xs font-semibold text-emerald-700 hover:underline">{open === s.id ? "Hide" : s.feedback > 0 ? "Feedback & visits" : "Visit log"}</button>
            {s.status === "active"
              ? <button data-testid={`share-revoke-${s.id}`} onClick={() => revoke(s.id)} className="ka-press rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50">Cancel link</button>
              : <span data-testid={`share-status-${s.id}`} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold capitalize text-slate-500">{s.status}</span>}
            {open === s.id && <VisitLog id={s.id} />}
          </li>
        ))}
      </ul>
    </Card>
  );
}
